import { LayoutOne } from "@/layouts";
import SeoHead from "@/components/SeoHead";
import PageViewBeacon from "@/components/PageViewBeacon";
import { useState, useEffect, useRef } from "react";
import BlogItemTwo from "@/components/blog/blogItemTwo";
import { Container, Row, Col } from "react-bootstrap";
import BlogSideBar from "@/components/blog/sidebar";
import { getLatestBlogs, getPosts } from "@/lib/blog";
import { getCategories, getTags, getAuthors } from "@/lib/cms";
import { BLOG_LIST_SCHEMA } from "@/lib/blogListSchema";
import ReactPaginate from "react-paginate";
import { FaAngleDoubleLeft, FaAngleDoubleRight } from "react-icons/fa";
import OurGroupSection from "@/components/ourGroup";

// react-paginate stamps role="navigation" and aria-label="Pagination" on
// its <ul>. ARIA disallows role=navigation on a <ul>, and the Lighthouse
// "Accessibility tree is not well-formed" audit fails the page when the
// role is inappropriate for the element. We wrap the paginator in a
// <nav> that owns the navigation semantics, then strip the offending
// attributes off the inner <ul> after mount so the tree stays valid.
function PaginationNav({ children }) {
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const ul = root.querySelector("ul");
    if (ul) {
      ul.removeAttribute("role");
      ul.removeAttribute("aria-label");
    }
  });
  return (
    <nav ref={rootRef} aria-label="Pagination">
      {children}
    </nav>
  );
}

// Compute how many posts reference each category / tag so the sidebar
// shows a real count even when the CMS endpoint omits it.
function enrichTaxonomyWithCounts(items, allPosts, key) {
  return items.map((item) => {
    const slug = typeof item === "string" ? item : item.slug;
    const name = typeof item === "string" ? item : item.name;
    const count = allPosts.reduce((acc, p) => {
      const list = Array.isArray(p[key]) ? p[key] : [];
      return list.some(
        (v) => String(v).toLowerCase() === String(slug).toLowerCase()
      )
        ? acc + 1
        : acc;
    }, 0);
    return { ...item, name, slug, count };
  });
}

export async function getStaticProps() {
  const [{ data: allPosts }, latest, categories, tags, authors] = await Promise.all([
    getPosts({ perPage: 100 }),
    getLatestBlogs(4),
    getCategories(),
    getTags(),
    getAuthors(),
  ]);
  return {
    props: {
      allPosts,
      latest,
      categories: enrichTaxonomyWithCounts(categories, allPosts, "category"),
      tags: enrichTaxonomyWithCounts(tags, allPosts, "tag"),
      authors,
    },
    revalidate: 3600,
  };
}

function Blog({ allPosts = [], latest = [], categories = [], tags = [], authors = [] }) {
  const perPageLimit = 6;
  const [currentItems, setCurrentItems] = useState(allPosts);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);

  useEffect(() => {
    const endOffset = itemOffset + perPageLimit;
    setCurrentItems(allPosts.slice(itemOffset, endOffset));
    setPageCount(Math.ceil(allPosts.length / perPageLimit));
  }, [itemOffset, perPageLimit, allPosts]);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * perPageLimit) % allPosts.length;
    setItemOffset(newOffset);
  };

  return (
    <>
      <PageViewBeacon contentType="blog_index" path="/blog" />
      <SeoHead
        path="/blog"
        fallbackTitle="Real Estate Blog | Siddhivinayak Group"
        fallbackDescription="Insights, guides, and market analysis for property buyers, sellers, and investors across India."
        publisherName="Siddhivinayak Group"
        seo={{
          seo_title: "Real Estate Blog | Siddhivinayak Group",
          meta_description: "Insights, guides, and market analysis for property buyers, sellers, and investors across India.",
          focus_keyword: "real estate blog India",
          secondary_keywords: [
            "property investment tips",
            "home loan guides",
            "RERA compliance",
            "luxury apartment buying",
            "Nagpur real estate news",
          ],
          og_image: "/img/og/siddhivinayak-group-og.jpg",
          schema_json: BLOG_LIST_SCHEMA,
        }}
        extraKeywords={["real estate insights India", "property market analysis"]}
      />
      <LayoutOne topbar={true}>
        <div className="ltn__blog-area mb-120">
          <Container>
            <Row>
              <Col xs={12} lg={8}>
                <Row className="blog-card-grid">
                  {currentItems.map((blog, key) => {
                    const slug = blog.slug;
                    return (
                      <Col key={key} xs={12} md={6}>
                        <BlogItemTwo
                          blogData={blog}
                          slug={slug}
                          baseUrl="blog"
                        />
                      </Col>
                    );
                  })}
                </Row>
                <Row>
                  <Col xs={12}>
                    <div className="ltn__pagination-area">
                      <PaginationNav>
                        <ReactPaginate
                          onPageChange={handlePageClick}
                          pageRangeDisplayed={3}
                          marginPagesDisplayed={2}
                          pageCount={pageCount}
                          nextLabel={<FaAngleDoubleRight />}
                          previousLabel={<FaAngleDoubleLeft />}
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          containerClassName="pagination ltn__pagination justify-content-center"
                          activeClassName="active"
                          renderOnZeroPageCount={null}
                        />
                      </PaginationNav>
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col xs={12} lg={{ span: 4, order: 0 }}>
                <BlogSideBar
                  latestdBlogs={latest}
                  categories={categories}
                  tags={tags}
                  authors={authors}
                  showAgent={false}
                  showBookForm={false}
                  showTopRated={false}
                  showAuthors={false}
                  showTwitter={false}
                  showFollowUs={false}
                />
              </Col>
            </Row>
          </Container>
        </div>
        <div className="section-bg-1">
          <OurGroupSection />
        </div>
      </LayoutOne>
    </>
  );
}

export default Blog;
